import { AVISO_PRIVACIDAD_PARTICIPANTE_TEXTO } from '../../participante-ui/infrastructure/avisoPrivacidadParticipanteText.ts';
import { renderAvisoParagraphMixed } from './foGesrPdfLayout.ts';
import {
  addImageFormatFromDataUrl,
  fetchStaticMapDataUrl,
  fitInBox,
  naturalImageSize,
} from './resolveImageForPdf.ts';
import type { FoGesrPdfDocument } from './foGesrPdfDocument.ts';

export type FoGesrYRef = { current: number };

export type FoGesrLandscapeLayout = {
  doc: FoGesrPdfDocument;
  y: FoGesrYRef;
  margin: number;
  pageH: number;
  usableW: number;
  setNormal: (size: number) => void;
  onNewPage: () => void;
};

export type FoGesrMapSectionLayout = FoGesrLandscapeLayout & {
  setBold: (size: number) => void;
};

/** Landscape A4 document, margins, and font helpers — FO-GESR forms. */
export async function createFoGesrLandscapeA4Base(): Promise<{
  doc: FoGesrPdfDocument;
  pageW: number;
  pageH: number;
  margin: number;
  usableW: number;
  y: FoGesrYRef;
  setNormal: (size: number) => void;
  setBold: (size: number) => void;
  newPage: () => void;
}> {
  const { FoGesrPdfDocument } = await import('./foGesrPdfDocument.ts');
  const doc = await FoGesrPdfDocument.createLandscapeA4();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  const usableW = pageW - margin * 2;
  const y: FoGesrYRef = { current: margin };
  const setNormal = (size: number) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
  };
  const setBold = (size: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
  };
  const newPage = () => {
    doc.addPage();
    y.current = margin;
  };
  return { doc, pageW, pageH, margin, usableW, y, setNormal, setBold, newPage };
}
/** Scale table column mm. */


export function scaleTableColumnMm(baseWidths: readonly number[], usableW: number): number[] {
  const sumW = baseWidths.reduce((a, b) => a + b, 0);
  const scale = usableW / sumW;
  return baseWidths.map((c) => c * scale);
}
/** Make fo gesr cell lines. */


export function makeFoGesrCellLines(
  doc: FoGesrPdfDocument,
  colW: number[],
  pad: number,
): (text: string, colIdx: number, fontSize: number) => string[] {
  return (text, colIdx, fontSize) => {
    doc.setFontSize(fontSize);
    const maxW = (colW[colIdx] ?? 0) - pad * 2;
    return doc.splitTextToSize((text || '—').trim() || '—', Math.max(maxW, 3));
  };
}

export type MetaPairsStyle = 'bold' | 'normal';
/** Write fo gesr meta label value pairs. */


export function writeFoGesrMetaLabelValuePairs(
  layout: FoGesrMapSectionLayout,
  metaPairs: [string, string][],
  style: MetaPairsStyle,
): void {
  const { doc, y, margin, pageH, usableW, setBold, setNormal, onNewPage } = layout;
  if (style === 'bold') setBold(9);
  else setNormal(9);
  for (const [lab, val] of metaPairs) {
    const line = `${lab} ${val}`.trim();
    const wrapped = doc.splitTextToSize(line, usableW);
    for (const ln of wrapped) {
      if (y.current + 5 > pageH - margin - 8) onNewPage();
      doc.text(ln, margin, y.current);
      y.current += 4.4;
    }
  }
  if (style === 'bold') setNormal(9);
  y.current += 2.5;
}
/** Write fo gesr aviso privacidad block. */


export function writeFoGesrAvisoPrivacidadBlock(
  doc: FoGesrPdfDocument,
  y: FoGesrYRef,
  margin: number,
  pageH: number,
  usableW: number,
  setNormal: (size: number) => void,
  onNewPage: () => void,
): void {
  setNormal(7);
  const marginTopAvisoMm = 5;
  if (y.current + marginTopAvisoMm > pageH - margin - 8) onNewPage();
  y.current += marginTopAvisoMm;
  const parrafosAviso = AVISO_PRIVACIDAD_PARTICIPANTE_TEXTO.split('\n').filter((p) => p.trim().length > 0);
  for (const para of parrafosAviso) {
    y.current = renderAvisoParagraphMixed({
      doc,
      yStart: y.current,
      margin,
      usableW,
      pageH,
      fontSize: 7,
      lineHeight: 3.5,
      paragraph: para,
      onNewPage,
    });
    y.current += 1.5;
  }
}

export type MapUbicacionInput = {
  lat: string;
  lon: string;
  /** Caption under the map; empty shows «—» when there is no map. */
  ubicacionDetalle: string;
  /** Centered map heading atop the FO-GESR LOCATION block as printed on the official Colombian PDF templates. */
  mapTitle: string;
};
/** Write fo gesr map ubicacion block. */


export async function writeFoGesrMapUbicacionBlock(
  layout: FoGesrMapSectionLayout,
  input: MapUbicacionInput,
): Promise<void> {
  const { doc, y, margin, pageH, usableW, setBold, setNormal, onNewPage } = layout;
  y.current += 2;
  if (y.current + 92 > pageH - margin - 8) onNewPage();

  const mapW = Math.min(usableW, 170);
  const mapMaxH = 76;
  const mapDataUrl = await fetchStaticMapDataUrl(input.lat, input.lon);
  let mapDrawn = false;
  if (mapDataUrl) {
    try {
      const fmt = addImageFormatFromDataUrl(mapDataUrl);
      const { w: nw, h: nh } = await naturalImageSize(mapDataUrl);
      const { dw, dh } = fitInBox(nw, nh, mapW, mapMaxH);
      const mx = margin + (usableW - dw) / 2;
      await doc.addImage(mapDataUrl, fmt, mx, y.current, dw, dh);
      y.current += dh + 4;
      mapDrawn = true;
    } catch {
      mapDrawn = false;
    }
  }

  setBold(10);
  doc.text(input.mapTitle, margin + usableW / 2, y.current, { align: 'center' });
  y.current += 6;
  setNormal(7);
  const ubiDetail = input.ubicacionDetalle;
  if (ubiDetail) {
    const ubiLines = doc.splitTextToSize(ubiDetail, usableW);
    for (const ln of ubiLines) {
      if (y.current + 3.5 > pageH - margin - 8) onNewPage();
      doc.text(ln, margin + usableW / 2, y.current, { align: 'center' });
      y.current += 3.5;
    }
  } else if (!mapDrawn) {
    doc.text('—', margin + usableW / 2, y.current, { align: 'center' });
    y.current += 4;
  }
  if (mapDrawn) {
    setNormal(7);
    doc.text('© Contribuidores de OpenStreetMap', margin + usableW, y.current + 2, { align: 'right' });
    y.current += 5;
  }
  y.current += 4;
}
